// Diário › Roda da Vida (0.65): nota de 0 a 10 para cada área, num gráfico de roda.
// Cada avaliação fica guardada com a data, para comparar com as anteriores e ver a evolução.
import { useState } from 'react';
import { Alert, Pressable, Text, View } from 'react-native';
import Svg, { Circle, G, Line, Polygon, Text as SvgText } from 'react-native-svg';
import { gravarRoda, lerRoda, useDiario, type AvaliacaoRoda } from '../../store/diario';
import { Btn, Chip, Field, Screen } from '../ui';
import { C, alpha } from '../theme';
import { Recolhe, dataLonga } from './comum';
import { today, uid } from '../../lib/date';
import { t } from '../../lib/i18n';
import { oferecerDesfazer } from '../../lib/desfazer';

/** Gráfico de roda (radar): a avaliação atual cheia e a anterior tracejada. */
export function GraficoRoda({ areas, notas, antes, cor, tam = 300 }: { areas: string[]; notas: Record<string, number>; antes?: Record<string, number> | null; cor: string; tam?: number }) {
  const c = tam / 2, R = tam / 2 - 46, n = Math.max(3, areas.length);
  const ponto = (i: number, v: number) => { const a = (Math.PI * 2 * i) / n - Math.PI / 2; return [c + Math.cos(a) * R * (v / 10), c + Math.sin(a) * R * (v / 10)]; };
  const pol = (ns: Record<string, number>) => areas.map((ar, i) => ponto(i, ns[ar] ?? 0).join(',')).join(' ');
  return (
    <Svg width={tam} height={tam}>
      {[2, 4, 6, 8, 10].map(v => <Circle key={v} cx={c} cy={c} r={(R * v) / 10} stroke={C.line} strokeWidth={1} fill="none" />)}
      {areas.map((ar, i) => { const [x, y] = ponto(i, 10); const [lx, ly] = ponto(i, 12.6); return (
        <G key={ar}>
          <Line x1={c} y1={c} x2={x} y2={y} stroke={C.line} strokeWidth={1} />
          <SvgText x={lx} y={ly} fill={C.sub} fontSize={11} textAnchor="middle" alignmentBaseline="middle">{t(ar).slice(0, 18)}</SvgText>
        </G>
      ); })}
      {!!antes && <Polygon points={pol(antes)} fill="none" stroke={C.mute} strokeWidth={2} strokeDasharray="6 4" />}
      <Polygon points={pol(notas)} fill={alpha(cor, 0.3)} stroke={cor} strokeWidth={2.5} />
      {areas.map((ar, i) => { const [x, y] = ponto(i, notas[ar] ?? 0); return <Circle key={ar} cx={x} cy={y} r={4} fill={cor} />; })}
    </Svg>
  );
}

export function RodaVida({ cor, onBack }: { cor: string; onBack: () => void }) {
  useDiario();
  const roda = lerRoda();
  /** Excluir uma avaliação (texto 05 C1): só o Desfazer de 5 segundos, sem Lixeira (o diário é cifrado e não vai aberto para a Lixeira). */
  const tirarAvaliacao = (av: AvaliacaoRoda) => {
    const r0 = lerRoda();
    const pos = r0.avaliacoes.findIndex(x => x.id === av.id);
    gravarRoda({ ...r0, avaliacoes: r0.avaliacoes.filter(x => x.id !== av.id) });
    oferecerDesfazer('excluir', () => {
      const r = lerRoda();
      if (r.avaliacoes.some(x => x.id === av.id)) return;
      const p = pos < 0 || pos > r.avaliacoes.length ? r.avaliacoes.length : pos;
      gravarRoda({ ...r, avaliacoes: [...r.avaliacoes.slice(0, p), av, ...r.avaliacoes.slice(p)] });
    });
  };
  const ultima = roda.avaliacoes[roda.avaliacoes.length - 1] ?? null;
  const [notas, setNotas] = useState<Record<string, number>>(() => ({ ...Object.fromEntries(roda.areas.map(a => [a, 5])), ...(ultima?.notas ?? {}) }));
  const [ver, setVer] = useState<AvaliacaoRoda | null>(null);
  const [editarAreas, setEditarAreas] = useState(false);
  const [nova, setNova] = useState('');
  const comparar = ver ? roda.avaliacoes[roda.avaliacoes.indexOf(ver) - 1] ?? null : ultima;
  const salvar = () => {
    const av: AvaliacaoRoda = { id: uid(), data: today(), notas: Object.fromEntries(roda.areas.map(a => [a, notas[a] ?? 0])) };
    gravarRoda({ ...roda, avaliacoes: [...roda.avaliacoes, av] });
    Alert.alert(t('Avaliação salva'), t('Ela fica guardada com a data de hoje para comparar depois.'));
  };
  const mudarArea = (antes: string, depois: string) => {
    if (!depois.trim() || roda.areas.includes(depois.trim())) return;
    gravarRoda({ areas: roda.areas.map(a => (a === antes ? depois.trim() : a)), avaliacoes: roda.avaliacoes.map(av => { const n = { ...av.notas }; if (antes in n) { n[depois.trim()] = n[antes]; delete n[antes]; } return { ...av, notas: n }; }) });
    setNotas(n => { const x = { ...n }; x[depois.trim()] = x[antes] ?? 5; delete x[antes]; return x; });
  };
  return (
    <Screen title={t('Roda da Vida')} onBack={onBack}>
      <View style={{ alignItems: 'center', backgroundColor: C.card, borderRadius: 16, paddingVertical: 8 }}>
        <GraficoRoda areas={roda.areas} notas={ver ? ver.notas : notas} antes={comparar?.notas} cor={cor} />
        <Text style={{ color: C.sub, fontSize: 11 }}>{ver ? t('Avaliação de {d}', { d: dataLonga(ver.data) }) : t('Nova avaliação')}{comparar ? ' · ' + t('tracejado: {d}', { d: dataLonga(comparar.data) }) : ''}</Text>
      </View>
      {ver ? (
        <Btn cor={cor} outline label={t('Voltar para a nova avaliação')} onPress={() => setVer(null)} />
      ) : (<>
        {roda.areas.map(a => (
          <View key={a} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: C.card, borderRadius: 14, padding: 10 }}>
            <Text style={{ color: C.text, flex: 1, fontWeight: '600' }}>{t(a)}</Text>
            <Pressable onPress={() => setNotas(n => ({ ...n, [a]: Math.max(0, (n[a] ?? 5) - 1) }))} style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center' }}><Text style={{ color: C.text, fontSize: 18 }}>−</Text></Pressable>
            <Text style={{ color: cor, fontWeight: '900', fontSize: 18, width: 30, textAlign: 'center' }}>{notas[a] ?? 5}</Text>
            <Pressable onPress={() => setNotas(n => ({ ...n, [a]: Math.min(10, (n[a] ?? 5) + 1) }))} style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center' }}><Text style={{ color: C.text, fontSize: 18 }}>+</Text></Pressable>
          </View>
        ))}
        <Btn cor={cor} label={t('Salvar avaliação de hoje')} onPress={salvar} />
        <Pressable onPress={() => setEditarAreas(x => !x)}><Text style={{ color: cor, fontWeight: '700' }}>✏️ {t('Renomear, adicionar ou tirar áreas')} {editarAreas ? '▾' : '▸'}</Text></Pressable>
        {editarAreas && (
          <View style={{ gap: 8 }}>
            {roda.areas.map(a => (
              <View key={a} style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                <Field defaultValue={t(a)} onEndEditing={e => mudarArea(a, e.nativeEvent.text)} style={{ flex: 1 }} />
                {roda.areas.length > 3 && <Pressable hitSlop={8} onPress={() => Alert.alert(t('Tirar a área "{a}"?', { a: t(a) }), '', [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Tirar'), style: 'destructive', onPress: () => gravarRoda({ ...roda, areas: roda.areas.filter(x => x !== a) }) }])}><Text style={{ color: C.danger }}>✕</Text></Pressable>}
              </View>
            ))}
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <Field value={nova} onChangeText={setNova} placeholder={t('Nova área (ex.: Espiritualidade)')} style={{ flex: 1 }} />
              <Chip cor={cor} on label={t('Adicionar')} onPress={() => { if (nova.trim() && !roda.areas.includes(nova.trim())) { gravarRoda({ ...roda, areas: [...roda.areas, nova.trim()] }); setNotas(n => ({ ...n, [nova.trim()]: 5 })); setNova(''); } }} />
            </View>
          </View>
        )}
      </>)}
      {!!roda.avaliacoes.length && (
        <View style={{ gap: 6, marginTop: 8 }}>
          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('EVOLUÇÃO')}</Text>
          {[...roda.avaliacoes].reverse().map((av, i, l) => {
            const media = Object.values(av.notas).reduce((s, x) => s + x, 0) / Math.max(1, Object.values(av.notas).length);
            const prev = l[i + 1]; const mPrev = prev ? Object.values(prev.notas).reduce((s, x) => s + x, 0) / Math.max(1, Object.values(prev.notas).length) : null;
            return (
              <Pressable key={av.id} onPress={() => setVer(av)} delayLongPress={1000} onLongPress={() => Alert.alert(t('Excluir esta avaliação?'), '', [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => tirarAvaliacao(av) }])}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: ver?.id === av.id ? alpha(cor, 0.15) : C.card, borderRadius: 12, padding: 10 }}>
                <Text style={{ color: C.text, flex: 1 }}>{dataLonga(av.data)}</Text>
                <Text style={{ color: C.text, fontWeight: '800' }}>{t('média {m}', { m: media.toFixed(1) })}</Text>
                {mPrev != null && <Text style={{ color: media >= mPrev ? C.ok : C.danger, fontWeight: '700' }}>{media >= mPrev ? '▲' : '▼'} {Math.abs(media - mPrev).toFixed(1)}</Text>}
              </Pressable>
            );
          })}
        </View>
      )}
      <View style={{ marginTop: 8 }}>
        <Recolhe titulo={'❓ ' + t('Como funciona a Roda da Vida')}>
          <Text style={{ color: C.sub, fontSize: 13, lineHeight: 19, paddingHorizontal: 4 }}>{t('Dê uma nota de 0 a 10 para como você se sente hoje em cada área da vida. O desenho mostra onde a sua vida está equilibrada e onde precisa de atenção. Salve a avaliação e repita de tempos em tempos (por exemplo, todo mês) para comparar e ver a sua evolução. Você pode renomear, adicionar ou tirar áreas.')}</Text>
        </Recolhe>
      </View>
      <View style={{ height: 60 }} />
    </Screen>
  );
}
